import { useState } from "react";

import clsx from "clsx";
import { difference, snakeCase } from "lodash";
import { Link } from "react-router-dom";

import DataExportCalendar from "@assets/data-export-calendar.svg";
import DataExportGcs from "@assets/data-export-gcs.svg";
import DataExportLogo from "@assets/data-export-logo.svg";
import { useUser } from "@components/user-context";
import { useLogger, usePrettyLogEventPage } from "@components/v2/analytics";
import {
  CREATE_DATA_EXPORT_PERFORMANCE_METRICS_CONFIG,
  FETCH_DATA_EXPORT_EMAIL_RECIPIENTS,
  FETCH_SCHEDULED_EXPORTS,
  FETCH_SCHEDULED_EXPORT_DETAILS,
  UPDATE_SCHEDULED_EXPORT,
} from "@gql/data-export";
import { useNorthbeamLazyQuery, useNorthbeamMutation } from "@hooks/http";
import {
  DataExportFormat,
  DataExportFrequency,
  DataExportMethod,
  DataExportType,
  type CreateDataExportPerformanceMetricsConfig,
  type CreateDataExportPerformanceMetricsConfigVariables,
  type DataExportPerformanceMetricsConfigInput,
  type FetchDataExportEmailRecipients,
  type SalesPageMetricSpec,
  type UpdateScheduledExport,
  type UpdateScheduledExportVariables,
} from "@north-beam/nb-client-side";
import {
  type Breakdown,
  type IGranularity,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { notifySuccess } from "@shared/notify";

import {
  type DataExportTarget,
  type ExportMethodConfig,
  type SalesMetricsConfig,
  type ScheduleConfig,
  type Step,
  type TimeFrame,
} from "../types";
import { generateDefaultScheduledExportName } from "../utils";

import { ExportMethodSelector } from "./export-method";
import { GCSConfigurator } from "./export-method/gcs-configurator";
import { Header } from "./header";
import { MetricsConfigurator } from "./metrics-configurator";
import { ScheduleConfigurator } from "./schedule-configurator";
import { SubmitScheduledExportButtonWithConfirmationModal } from "./schedule-configurator/submit-scheduled-export-button-with-confirmation-modal";

export const DATA_EXPORT_TARGETS: Record<DataExportTarget, string> = {
  "performance-metrics": "PERFORMANCE_METRICS",
} as const;

interface Props {
  isOpen: boolean;
  initialStep?: Step;
  target: DataExportTarget;
  availableMetricSpecs: SalesPageMetricSpec[];
  selectedMetricIds: string[];
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  nbAccountingMode: INBAccountingMode;
  granularity: IGranularity;
  timeGranularity: ITimeGranularityV2;
  breakdowns: Breakdown[];
  dateRange: TimeFrame;
  onClose(): void;
  onAfterSave?(): Promise<void>;
  existingScheduledExportId?: string;
  initialExportMethod?: DataExportMethod;
  initialExportMethodDetails?: string;
  initialExportConfig?: SalesMetricsConfig;
  initialScheduleConfig?: ScheduleConfig;
  hideExportNowButton?: boolean;
}

export function buildDefaultFileName(
  target: DataExportTarget,
  companyName: string | null,
) {
  const prefix = "sales_data";

  if (!companyName) {
    return target;
  }

  return `${prefix}-${snakeCase(companyName)}`;
}

export function DataExportBuilderModal(props: Props) {
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="2xl"
      bodyClassName="px-0 py-0 h-full"
      contentClassName="h-[calc(100%-40px)] overflow-hidden"
      headerProps={{ className: "absolute right-0 top-0" }}
    >
      {props.isOpen && <DataExportBuilderModalContent {...props} />}
    </Modal>
  );
}

function DataExportBuilderModalContent({
  onClose,
  onAfterSave,
  target,
  availableMetricSpecs,
  selectedMetricIds,
  attributionModel,
  attributionWindow,
  nbAccountingMode,
  granularity,
  timeGranularity,
  breakdowns,
  dateRange,
  initialStep,
  existingScheduledExportId,
  initialExportMethod,
  initialExportMethodDetails,
  initialExportConfig,
  initialScheduleConfig,
  hideExportNowButton,
}: Props) {
  const isEditingExistingDataExport = !!existingScheduledExportId;

  const { user } = useUser();
  const prettyLogEventPage = usePrettyLogEventPage();
  const logger = useLogger({ container: "data-export-menu" });

  const defaultInitialStep = user.featureFlags.enableDataExportExternalGCS
    ? "method-selector"
    : "metrics-configurator";

  const [step, setStep] = useState<Step>(initialStep ?? defaultInitialStep);
  const [isScheduleConfiguratorVisible, setScheduleConfiguratorVisible] =
    useState(false);

  const [exportMethodConfig, setExportMethodConfig] =
    useState<ExportMethodConfig>({
      method: initialExportMethod ?? DataExportMethod.NORTHBEAM_GCS_BUCKET,
      details: initialExportMethodDetails ?? undefined,
    });

  const [exportConfig, setExportConfig] = useState<SalesMetricsConfig>({
    metrics: [],
    exportAggregation: "breakdown",
    removeZeroSpend: false,
    aggregateData: false,
    includeIds: false,
    includeKindAndPlatform: false,
    level: "platform",
    timeGranularity: "daily",
    breakdowns: [],
    defaultAttributionModels: [],
    defaultAttributionWindows: [],
    defaultAccountingModes: [],
    timeFrame: "last7Days",
    ...initialExportConfig,
    fileName:
      initialExportConfig?.fileName ??
      buildDefaultFileName(target, user.companyName),
  });

  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>(() => ({
    name: generateDefaultScheduledExportName(),
    frequency: DataExportFrequency.DAILY,
    timeFrames: [],
    recipients: [],
    ...initialScheduleConfig,
  }));

  const [loading, setLoading] = useState(false);

  const {
    loadData: loadEmailRecipientsForDataExport,
    data: {
      data: fetchedEmailRecipientsForDataExport,
      loading: isLoadingEmailRecipients,
    },
  } = useNorthbeamLazyQuery<FetchDataExportEmailRecipients>(
    FETCH_DATA_EXPORT_EMAIL_RECIPIENTS,
    { skip: !isScheduleConfiguratorVisible },
  );

  const [createDataExportPerformanceMetricsConfig] = useNorthbeamMutation<
    CreateDataExportPerformanceMetricsConfig,
    CreateDataExportPerformanceMetricsConfigVariables
  >(CREATE_DATA_EXPORT_PERFORMANCE_METRICS_CONFIG);

  const [updateDataExport] = useNorthbeamMutation<
    UpdateScheduledExport,
    UpdateScheduledExportVariables
  >(UPDATE_SCHEDULED_EXPORT, {
    refetchQueries: [
      { query: FETCH_SCHEDULED_EXPORTS, variables: { page: 1 } },
      {
        query: FETCH_SCHEDULED_EXPORT_DETAILS,
        variables: { id: existingScheduledExportId },
      },
    ],
    awaitRefetchQueries: true,
  });

  const createOrUpdateExport = (type: DataExportType) => {
    let timeFrameType: string;
    let timeFrameStartDate: string | null | undefined;
    let timeFrameEndDate: string | null | undefined;

    if (typeof exportConfig.timeFrame === "string") {
      timeFrameType = exportConfig.timeFrame;
    } else {
      timeFrameType = exportConfig.timeFrame.type;
      timeFrameStartDate = exportConfig.timeFrame.startDate;
      timeFrameEndDate = exportConfig.timeFrame.endDate;
    }

    const data: DataExportPerformanceMetricsConfigInput = {
      target: DATA_EXPORT_TARGETS[target],
      formats: [DataExportFormat.CSV],
      exportMethod: exportMethodConfig.method,
      exportMethodDetails: exportMethodConfig.details,
      exportType: type,
      exportAggregation: exportConfig.exportAggregation,
      removeZeroSpend: exportConfig.removeZeroSpend,
      aggregateData: exportConfig.aggregateData,
      includeIds: exportConfig.includeIds,
      includeKindAndPlatform: exportConfig.includeKindAndPlatform,
      level: exportConfig.level,
      timeGranularity: exportConfig.timeGranularity,
      exportFileName: exportConfig.fileName,
      defaultAttributionModels: exportConfig.defaultAttributionModels,
      defaultAttributionWindows: exportConfig.defaultAttributionWindows,
      defaultAccountingModes: exportConfig.defaultAccountingModes,
      timeFrameType,
      timeFrameStartDate,
      timeFrameEndDate,
      breakdowns: exportConfig.breakdowns.map((e, index) => ({
        position: index,
        key: e.key,
        values: e.values,
      })),
      metrics: exportConfig.metrics.map((e, index) => ({
        id: e.id,
        position: index,
        label: e.label,
        attributionModels: Array.from(e.attributionModels),
        attributionWindows: Array.from(e.attributionWindows),
        accountingModes: Array.from(e.accountingModes),
      })),
      ...(type === DataExportType.SCHEDULED
        ? {
            scheduleName: scheduleConfig.name,
            frequency: scheduleConfig.frequency,
            scheduleTimeFrames: scheduleConfig.timeFrames,
            recipients: scheduleConfig.recipients,
          }
        : null),
    };

    if (type === DataExportType.ONE_OFF) {
      logger.event.salesPageExportNow("submit", {
        eventDetails: {
          details: data,
        },
      });
    } else {
      logger.event.salesPageExportScheduled("submit", {
        eventDetails: {
          details: data,
        },
      });
    }

    if (isEditingExistingDataExport) {
      return updateDataExport({
        variables: { id: existingScheduledExportId, data },
      });
    }

    return createDataExportPerformanceMetricsConfig({ variables: { data } });
  };

  const getExternalUsersFromScheduleConfig = () => {
    const internalUsers =
      fetchedEmailRecipientsForDataExport?.me.dataExportEmailRecipients ?? [];
    return difference(scheduleConfig.recipients, internalUsers);
  };
  return (
    <div className="h-full grid grid-rows-[5.5rem_auto_4.5rem] overflow-hidden">
      <div className="flex justify-center items-center border-b border-gray-100">
        {step === "method-selector" && (
          <Header
            title="Data export: Report builder"
            subtitle="Schedule, export and share your data via CSV or Google Cloud Storage"
            img={
              <img
                className="w-[25px] ml-3 mb-2"
                alt="data-export-logo"
                src={DataExportLogo}
              />
            }
          />
        )}
        {step === "gcs-configurator" && (
          <Header
            title="Google Cloud Storage"
            subtitle="Schedule, export and share your data to Google Cloud Storage"
            img={
              <img
                className="w-[24px] ml-3"
                alt="data-export-logo"
                src={DataExportGcs}
              />
            }
          />
        )}
        {step === "metrics-configurator" && !isScheduleConfiguratorVisible && (
          <Header
            title="Data export: Report builder"
            subtitle="Schedule, export and share your data via CSV"
            img={
              <img
                className="w-[25px] ml-3 mb-2"
                alt="data-export-logo"
                src={DataExportLogo}
              />
            }
          />
        )}
        {isScheduleConfiguratorVisible && (
          <Header
            title="Schedule export"
            subtitle="Schedule and export your up to date data"
            img={
              <img
                className="w-[24px] ml-3"
                alt="data-export-logo"
                src={DataExportCalendar}
              />
            }
          />
        )}
      </div>
      <div className="overflow-hidden relative">
        {isScheduleConfiguratorVisible && (
          <ScheduleConfigurator
            scheduleConfig={scheduleConfig}
            exportMethodConfig={exportMethodConfig}
            initialRecipients={
              fetchedEmailRecipientsForDataExport?.me
                .dataExportEmailRecipients ?? []
            }
            isLoadingEmailRecipients={isLoadingEmailRecipients}
            loadEmailRecipients={loadEmailRecipientsForDataExport}
            onChangeSchedule={setScheduleConfig}
            onClose={() => {
              prettyLogEventPage({
                domain: "DataExport",
                action: "onClose",
                additionalActionInfo: "Schedule Configurator closed",
              });

              setScheduleConfiguratorVisible(false);
            }}
          />
        )}
        {step === "method-selector" && (
          <ExportMethodSelector onSelect={setStep} />
        )}
        {step === "gcs-configurator" && (
          <GCSConfigurator
            onExportMethodConfigured={setExportMethodConfig}
            initialBucketName={exportMethodConfig.details}
          />
        )}
        {step === "metrics-configurator" && (
          <MetricsConfigurator
            target={target}
            selectedMetricIds={selectedMetricIds}
            availableMetricSpecs={availableMetricSpecs}
            attributionModel={attributionModel}
            defaultAttributionModels={exportConfig.defaultAttributionModels}
            attributionWindow={attributionWindow}
            defaultAttributionWindows={exportConfig.defaultAttributionWindows}
            nbAccountingMode={nbAccountingMode}
            defaultAccountingModes={exportConfig.defaultAccountingModes}
            granularity={granularity}
            timeGranularity={timeGranularity}
            breakdowns={breakdowns}
            dateRange={dateRange}
            initialExportAggregation={exportConfig.exportAggregation}
            initialRemoveZeroSpend={exportConfig.removeZeroSpend}
            initialAggregateData={exportConfig.aggregateData}
            initialIncludeIds={exportConfig.includeIds}
            initialIncludeKindAndPlatform={exportConfig.includeKindAndPlatform}
            fileName={exportConfig.fileName}
            onChange={setExportConfig}
          />
        )}
      </div>
      <div className="flex justify-between items-center px-6 w-full border-t border-gray-100">
        <div className="text-sm">
          <span className="font-semibold">
            Need help exporting your report?
          </span>
          <span>
            {" "}
            Submit a support request{" "}
            <a
              href="https://www.northbeam.io/submit-a-support-ticket"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>
          </span>
        </div>
        <div className="flex ml-5 space-x-4">
          {step === "gcs-configurator" && (
            <PrimaryButton
              isDisabled={
                exportMethodConfig.method !==
                DataExportMethod.EXTERNAL_GCS_BUCKET
              }
              onClick={() => {
                prettyLogEventPage({
                  domain: "DataExport",
                  action: "onClick",
                  additionalActionInfo: "Select data export method",
                });

                setStep("metrics-configurator");
              }}
            >
              NEXT
            </PrimaryButton>
          )}
          {step === "metrics-configurator" &&
            !isScheduleConfiguratorVisible && (
              <>
                {user.featureFlags.enableDataExportSchedule && (
                  <SecondaryButton
                    className="pt-1 pr-3 pb-1 pl-3 text-xs font-normal"
                    onClick={() => {
                      prettyLogEventPage({
                        domain: "DataExport",
                        action: "onClick",
                        additionalActionInfo: "Schedule Configurator opened",
                      });
                      if (!isEditingExistingDataExport) {
                        setScheduleConfig({
                          name: generateDefaultScheduledExportName(),
                          frequency: DataExportFrequency.DAILY,
                          timeFrames: [{ day: "*", time: "08:00" }],
                          recipients: [],
                        });
                      }
                      setScheduleConfiguratorVisible(true);
                    }}
                  >
                    {isEditingExistingDataExport ? "UPDATE" : "SCHEDULE EXPORT"}
                    <i className="fa-regular fa-sharp fa-calendar-clock text-[12px] ml-1.5"></i>
                  </SecondaryButton>
                )}
                {!isEditingExistingDataExport && !hideExportNowButton && (
                  <PrimaryButton
                    isDisabled={
                      loading ||
                      !exportConfig.fileName ||
                      exportConfig.fileName.length < 3 ||
                      exportConfig.metrics.length === 0
                    }
                    loading={loading}
                    className="min-w-[110px] min-h-[32px] flex justify-center items-center"
                    onClick={async () => {
                      prettyLogEventPage({
                        domain: "DataExport",
                        action: "onClick",
                        data: {
                          exportType: DataExportType.ONE_OFF,
                          exportMethod: exportMethodConfig.method,
                        },
                        additionalActionInfo: "Create Data Export button",
                      });

                      setLoading(true);
                      await createOrUpdateExport(DataExportType.ONE_OFF);
                      await onAfterSave?.();
                      setLoading(false);

                      notifySuccess({
                        message: "Your export has started!",
                        caption: (
                          <>
                            It may take up to a few minutes to complete
                            depending on the size of the export. When your
                            export is done it will live in the{" "}
                            {exportMethodConfig.method ===
                              DataExportMethod.EXTERNAL_GCS_BUCKET &&
                              `"${exportMethodConfig.details}" bucket`}
                            {exportMethodConfig.method ===
                              DataExportMethod.NORTHBEAM_GCS_BUCKET && (
                              <Link
                                to="./customers/documents"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Documents page.
                              </Link>
                            )}
                          </>
                        ),
                      });

                      onClose();
                    }}
                  >
                    EXPORT NOW
                  </PrimaryButton>
                )}
              </>
            )}
          {isScheduleConfiguratorVisible && (
            <SubmitScheduledExportButtonWithConfirmationModal
              buttonLabel={
                isEditingExistingDataExport
                  ? "CONFIRM CHANGES"
                  : "SCHEDULE EXPORT"
              }
              className="min-w-[150px] min-h-[32px] flex justify-center items-center"
              externalUsers={getExternalUsersFromScheduleConfig()}
              isDisabled={
                loading ||
                scheduleConfig.timeFrames.length === 0 ||
                !scheduleConfig.name ||
                (exportMethodConfig.method !==
                  DataExportMethod.EXTERNAL_GCS_BUCKET &&
                  scheduleConfig.recipients.length === 0)
              }
              isLoading={loading}
              onSubmit={async () => {
                prettyLogEventPage({
                  domain: "DataExport",
                  action: "onClick",
                  data: {
                    exportType: DataExportType.SCHEDULED,
                    exportMethod: exportMethodConfig.method,
                  },
                  additionalActionInfo: isEditingExistingDataExport
                    ? "Edit Scheduled Export button"
                    : "Create Data Export button",
                });

                setLoading(true);
                await createOrUpdateExport(DataExportType.SCHEDULED);
                await onAfterSave?.();
                setLoading(false);

                notifySuccess({
                  message: isEditingExistingDataExport
                    ? "Your export has been edited!"
                    : "Your export has been scheduled!",
                  caption: (
                    <>
                      When your export is done it will live in the{" "}
                      {exportMethodConfig.method ===
                        DataExportMethod.EXTERNAL_GCS_BUCKET &&
                        `"${exportMethodConfig.details}" bucket`}
                      {exportMethodConfig.method ===
                        DataExportMethod.NORTHBEAM_GCS_BUCKET && (
                        <Link
                          to="./customers/documents"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Documents page.
                        </Link>
                      )}
                    </>
                  ),
                });

                onClose();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
